/**
 * 1.6.0 AI content description: Workflows → "New from template" (plan C2): the four example flows (AI_FLOW_TEMPLATES:
 * Claude, OpenAI, Gemini, OpenRouter), each with what it does, where the media goes and the provider's retention note.
 * Making one asks for its webhook endpoint (an existing one, or Administration → Webhooks → Add from a template) and
 * the log layer to write, builds the flow for that endpoint and layer (aiTemplateGraph: a description for a text
 * layer, one of the options for a vocabulary layer, a list for a tags layer; 1.6.2), saves the draft and opens it.
 * Stream A's file (Workflows.tsx only mounts it, for automation.manage).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import {
  AI_FLOW_TEMPLATES, TEMPLATE_STRATUM_ID, aiTemplateGraph, aiTemplateLayerProblem, aiTemplateName, aiTemplateSummary,
} from '@prism/contracts';
import { api, errorMessage } from '../../api/client';
import { useStrata } from '../../api/hooks';
import { useWebhookEndpoints } from '../../api/hooks/automation';
import { queryKeys } from '../../api/queryKeys';
import { Modal } from '../../components/Modal';
import { toast } from '../../components/Toasts';

export function AiFlowTemplatesButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="btn" onClick={() => setOpen(true)} data-testid="new-from-template"><Sparkles size={15} /> New from template</button>
      {open && <TemplateDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function TemplateDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const endpoints = (useWebhookEndpoints().data?.items ?? []).filter((e) => e.enabled);
  const layers = (useStrata().data?.items ?? []).filter((s) => !s.system && !s.archived);
  const [id, setId] = useState(AI_FLOW_TEMPLATES[0]?.id ?? '');
  const t = AI_FLOW_TEMPLATES.find((x) => x.id === id) ?? AI_FLOW_TEMPLATES[0];
  // The provider's own endpoint first (by its URL's host), else the first one.
  const host = (u: string) => { try { return new URL(u).host; } catch { return ''; } };
  const matching = t ? endpoints.find((e) => host(e.url) === host(t.endpoint.url)) : undefined;
  const [endpointId, setEndpointId] = useState('');
  const [stratumId, setStratumId] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const chosenEndpoint = endpointId || matching?.id || endpoints[0]?.id || '';
  const chosenLayer = stratumId || (layers.some((l) => l.id === TEMPLATE_STRATUM_ID) ? TEMPLATE_STRATUM_ID : layers[0]?.id ?? '');
  const endpoint = endpoints.find((e) => e.id === chosenEndpoint);
  const layer = layers.find((l) => l.id === chosenLayer);
  const layerProblem = layer ? aiTemplateLayerProblem(layer) : null;
  if (!t) return null;
  const defaultName = layer ? aiTemplateName(t, layer) : t.name;

  async function create() {
    if (!t || !chosenEndpoint || !layer || layerProblem) return;
    setBusy(true);
    try {
      const r = await api<{ id: string }>('/api/v1/automations', { method: 'POST', json: { name: name.trim() || defaultName } });
      await api(`/api/v1/automations/${r.id}/draft`, { method: 'PUT', json: { baseRevision: 1, graph: aiTemplateGraph(t, layer, chosenEndpoint) } });
      await qc.invalidateQueries({ queryKey: queryKeys.automations() });
      onClose();
      navigate(`/workflows/edit/${r.id}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="New workflow from a template" wide onClose={onClose} actions={(
      <>
        <button className="btn ghost" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={busy || !chosenEndpoint || !layer || Boolean(layerProblem)} onClick={() => void create()} data-testid="template-create">Create the draft</button>
      </>
    )}>
      <div className="ai-template-grid" role="radiogroup" aria-label="Templates">
        {AI_FLOW_TEMPLATES.map((x) => (
          <button key={x.id} type="button" role="radio" aria-checked={x.id === t.id} className={`ai-template-card ${x.id === t.id ? 'selected' : ''}`}
            onClick={() => setId(x.id)} data-testid="flow-template" data-provider={x.provider}>
            <strong>{x.name}</strong>
            <span>{x.description}</span>
          </button>
        ))}
      </div>
      <div className="ai-template-fields">
        <div className="field"><label htmlFor="tpl-name">Name</label>
          <input id="tpl-name" className="input" value={name} placeholder={defaultName}onChange={(e) => setName(e.target.value)} data-testid="template-name" />
        </div>
        <div className="field"><label htmlFor="tpl-endpoint">Webhook endpoint</label>
          <select id="tpl-endpoint" className="select" value={chosenEndpoint} onChange={(e) => setEndpointId(e.target.value)} data-testid="template-endpoint">
            {!endpoints.length && <option value="">No endpoints yet</option>}
            {endpoints.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <div className="field"><label htmlFor="tpl-layer">Write the answers to</label>
          <select id="tpl-layer" className="select" value={chosenLayer} onChange={(e) => setStratumId(e.target.value)} data-testid="template-layer"
            aria-describedby="tpl-layer-hint">
            {layers.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select>
          {layer && (
            <span id="tpl-layer-hint" className={layerProblem ? 'error-text' : 'help'} role={layerProblem ? 'alert' : undefined} data-testid="template-layer-hint">
              {layerProblem ?? aiTemplateSummary(layer)}
            </span>
          )}
        </div>
      </div>
      <p className="help">
        {endpoints.length ? `The ${t.endpoint.name} endpoint is ${t.endpoint.url}, with the API key in the ${t.endpoint.apiKeyHeader} header.` : 'An administrator adds the endpoint in Administration → Webhooks → Add from a template.'}
        {endpoint && !endpoint.mayReceiveMediaLinks && ' This endpoint may not receive media links yet: allow it in Administration → Webhooks before publishing.'}
        {' '}The answers are always suggestions: people accept or reject them in the Log tab, and nothing a person wrote is changed.
      </p>
      <div className="ai-template-note" data-testid="template-data-note">
        <strong>Where the media goes</strong>
        <p>{t.dataNote}</p>
        {t.notes?.length ? (
          <details>
            <summary>Notes on this template</summary>
            {t.notes.map((n) => <p key={n} className="faint">{n}</p>)}
          </details>
        ) : null}
      </div>
    </Modal>
  );
}
